import React from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Flag, MessageSquare, User, FileText } from 'lucide-react';

export interface Report {
  id: string;
  contentType: string;
  contentId: string;
  reportedBy: string;
  reporterName: string;
  targetUser: string;
  targetName: string;
  reason: string;
  details: string;
  createdAt: Date;
  status: string;
}

interface ReportedContentProps {
  reports: Report[];
  onUpdateReportStatus: (reportId: string, status: string) => void;
}

const ReportedContent: React.FC<ReportedContentProps> = ({ reports, onUpdateReportStatus }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Reported Content</CardTitle>
        <CardDescription>
          Review and moderate reported content.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {reports.length === 0 ? (
          <div className="text-center py-12">
            <Flag size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Reported Content</h3>
            <p className="text-muted-foreground">
              There are currently no reports to review.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {reports.map((report) => (
              <div key={report.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={
                        report.contentType === 'message' ? 'bg-blue-500' :
                        report.contentType === 'profile' ? 'bg-purple-DEFAULT' :
                        'bg-amber-500'
                      }>
                        {report.contentType.charAt(0).toUpperCase() + report.contentType.slice(1)}
                      </Badge>
                      <Badge variant="outline" className={
                        report.status === 'pending' ? 'border-amber-500 text-amber-500' :
                        report.status === 'investigating' ? 'border-blue-500 text-blue-500' :
                        report.status === 'resolved' ? 'border-green-500 text-green-500' :
                        'border-red-500 text-red-500'
                      }>
                        {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        Reported {new Date(report.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="font-medium">
                      {report.reason}
                    </h3>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant={report.status === 'investigating' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={() => onUpdateReportStatus(report.id, 'investigating')}
                      disabled={report.status === 'resolved' || report.status === 'dismissed'}
                    >
                      Investigate
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-green-500 border-green-500 hover:bg-green-500/10"
                      onClick={() => onUpdateReportStatus(report.id, 'resolved')}
                      disabled={report.status === 'resolved' || report.status === 'dismissed'}
                    >
                      Resolve
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-500 border-red-500 hover:bg-red-500/10"
                      onClick={() => onUpdateReportStatus(report.id, 'dismissed')}
                      disabled={report.status === 'resolved' || report.status === 'dismissed'}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <div className="text-sm font-medium mb-1">Reported By</div>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback>{report.reporterName.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>{report.reporterName}</div>
                      <Badge variant="outline" className="text-xs">{report.reportedBy}</Badge>
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-sm font-medium mb-1">Target</div>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback>{report.targetName.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>{report.targetName}</div>
                      <Badge variant="outline" className="text-xs">{report.targetUser}</Badge>
                    </div>
                  </div>
                </div>
                
                <div className="bg-muted p-3 rounded-md text-sm mb-4">
                  <div className="font-medium mb-1">Report Details:</div>
                  <p>{report.details}</p>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" className="text-xs">
                    <MessageSquare className="h-3 w-3 mr-1" />
                    Message Reporter
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs">
                    <User className="h-3 w-3 mr-1" />
                    Message Reported User
                  </Button>
                  <Button variant="outline" size="sm" className="text-xs">
                    <FileText className="h-3 w-3 mr-1" />
                    View Content
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReportedContent;
